/**
 * ExpressDiagnosisPage — Mode Express SADASkinCare
 *
 * Diagnostic court à 4 questions, conçu pour réduire les refus clients :
 *  - Étape 1 : Identification (téléphone OU sans téléphone → code SK-XXXX)
 *  - Étape 2-5 : 4 questions (Zone · Préoccupation · Type peau · Allergie)
 *  - Étape 6 : Résultat avec carte branded pharmacie (à photographier par le client)
 *
 * Photo facultative (non demandée dans ce mode). Sortie : conseils + produits.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Sparkles, Phone, UserX, Loader2, Check,
  Copy, Printer, Camera, Building2, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Configuration des questions ────────────────────────────────────────────
const ZONES = [
  { id: 'visage', label: 'Visage', emoji: '🧴' },
  { id: 'corps', label: 'Corps', emoji: '💆' },
  { id: 'cheveux', label: 'Cheveux', emoji: '💇' },
];

const CONCERNS = [
  { id: 'acne', label: 'Acné / Imperfections', emoji: '🔴' },
  { id: 'taches', label: 'Taches / Pigmentation', emoji: '🟤' },
  { id: 'secheresse', label: 'Sécheresse', emoji: '🌵' },
  { id: 'sensibilite', label: 'Sensibilité / Rougeurs', emoji: '🌹' },
  { id: 'anti_age', label: 'Anti-âge / Rides', emoji: '⏳' },
  { id: 'autre', label: 'Autre / Je ne sais pas', emoji: '❓' },
];

const SKIN_TYPES = [
  { id: 'seche', label: 'Sèche' },
  { id: 'grasse', label: 'Grasse' },
  { id: 'mixte', label: 'Mixte' },
  { id: 'normale', label: 'Normale' },
  { id: 'inconnu', label: 'Je ne sais pas' },
];

const ALLERGY_OPTIONS = [
  { id: 'non', label: 'Non' },
  { id: 'pas_sur', label: 'Pas sûr·e' },
  { id: 'oui', label: 'Oui' },
];

// ── Carte de choix (case à cliquer style "card") ──────────────────────────
const ChoiceCard = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left p-4 rounded-xl border-2 transition ${
      active
        ? 'border-amber-500 bg-amber-50 shadow-sm'
        : 'border-zinc-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
    }`}
  >
    {children}
  </button>
);

const StepHeader = ({ step, total, title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-bold tracking-widest text-amber-700 uppercase">
        Étape {step}/{total}
      </span>
      <div className="flex-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
          style={{ width: `${(step / total) * 100}%` }}
        />
      </div>
    </div>
    <h2 className="text-2xl font-bold text-zinc-900">{title}</h2>
    {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
  </div>
);

// ════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════
const ExpressDiagnosisPage = ({ onBack, onComplete = null, initialIdentification = null, embedded = false }) => {
  // If we receive identification from parent funnel, start at step 2 (zone) directly
  const [step, setStep] = useState(initialIdentification ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const TOTAL_STEPS = 5;

  // Étape 1 : identification
  const [idMode, setIdMode] = useState(initialIdentification?.mode || 'phone');      // 'phone' | 'code'
  const [phone, setPhone] = useState(initialIdentification?.phone || '');
  const [firstName, setFirstName] = useState(initialIdentification?.first_name || '');
  const [birthYear, setBirthYear] = useState(initialIdentification?.birth_year ? String(initialIdentification.birth_year) : '');
  const [existingCode, setExistingCode] = useState(initialIdentification?.client_code || '');

  // Étapes 2-5 : réponses
  const [zone, setZone] = useState(null);
  const [concern, setConcern] = useState(null);
  const [skinType, setSkinType] = useState(null);
  const [allergy, setAllergy] = useState(null);
  const [allergyDetail, setAllergyDetail] = useState('');

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS + 1));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const canNextStep1 = () => {
    if (!firstName.trim()) return false;
    if (idMode === 'phone' && !phone.trim()) return false;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const r = await axios.post(`${API_URL}/comprehensive/diagnosis/express`, {
        identification: {
          mode: idMode,
          phone: idMode === 'phone' ? phone.trim() : null,
          first_name: firstName.trim(),
          birth_year: birthYear ? parseInt(birthYear, 10) : null,
          existing_client_code: existingCode.trim().toUpperCase() || null,
        },
        answers: {
          zone, concern, skin_type: skinType, allergy,
          allergy_detail: allergyDetail.trim() || null,
        },
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Embedded mode: hand result to parent funnel (skip standalone result card)
      if (onComplete) {
        onComplete(r.data);
        return;
      }
      setResult(r.data);
      setStep(TOTAL_STEPS + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur diagnostic express');
    } finally { setSubmitting(false); }
  };

  // ─── RENDER : RÉSULTAT FINAL ─────────────────────────────────────────────
  if (result) {
    return <ExpressResultCard result={result} onBack={onBack} onRestart={() => { setResult(null); setStep(1); setPhone(''); setFirstName(''); setExistingCode(''); setZone(null); setConcern(null); setSkinType(null); setAllergy(null); setAllergyDetail(''); }} />;
  }

  // ─── RENDER : ÉTAPES ─────────────────────────────────────────────────────
  // When embedded, render without page chrome (parent funnel handles header/wrapping)
  const stepsContent = (
    <>
      <Card className="shadow-lg border-amber-200">
          <CardContent className="p-6">
            {/* === Étape 1 : Identification === */}
            {step === 1 && (
              <>
                <StepHeader step={1} total={TOTAL_STEPS} title="Identification" subtitle="Téléphone recommandé pour retrouver la fiche au prochain passage." />
                <div className="flex gap-2 mb-4" data-testid="express-id-mode">
                  <button
                    onClick={() => setIdMode('phone')}
                    className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium ${idMode === 'phone' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-zinc-200 bg-white text-zinc-600'}`}
                    data-testid="express-id-phone"
                  >
                    <Phone className="w-4 h-4 inline mr-1" /> Avec téléphone
                  </button>
                  <button
                    onClick={() => setIdMode('code')}
                    className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium ${idMode === 'code' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-zinc-200 bg-white text-zinc-600'}`}
                    data-testid="express-id-code"
                  >
                    <UserX className="w-4 h-4 inline mr-1" /> Sans téléphone
                  </button>
                </div>
                <div className="space-y-3">
                  {idMode === 'phone' && (
                    <div>
                      <Label className="text-xs font-medium">Téléphone *</Label>
                      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07 XX XX XX XX" data-testid="express-phone" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">Prénom *</Label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ex : Awa" data-testid="express-first-name" />
                  </div>
                  {idMode === 'code' && (
                    <>
                      <div>
                        <Label className="text-xs font-medium">Année de naissance (optionnel)</Label>
                        <Input type="number" min="1900" max="2025" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} placeholder="1995" data-testid="express-birth-year" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Code client existant (si déjà venu)</Label>
                        <Input value={existingCode} onChange={(e) => setExistingCode(e.target.value)} placeholder="SK-XXXX" className="font-mono" data-testid="express-existing-code" />
                        <p className="text-[10px] text-zinc-400 mt-0.5">Saisir le code du dernier passage pour retrouver l'historique.</p>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* === Étape 2 : Zone === */}
            {step === 2 && (
              <>
                <StepHeader step={2} total={TOTAL_STEPS} title="Quelle zone à traiter ?" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="express-zone-grid">
                  {ZONES.map((z) => (
                    <ChoiceCard key={z.id} active={zone === z.id} onClick={() => setZone(z.id)} testId={`express-zone-${z.id}`}>
                      <div className="text-center">
                        <div className="text-3xl mb-1">{z.emoji}</div>
                        <div className="font-semibold">{z.label}</div>
                      </div>
                    </ChoiceCard>
                  ))}
                </div>
              </>
            )}

            {/* === Étape 3 : Préoccupation === */}
            {step === 3 && (
              <>
                <StepHeader step={3} total={TOTAL_STEPS} title="Préoccupation principale ?" subtitle="Choisissez la plus gênante." />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="express-concern-grid">
                  {CONCERNS.map((c) => (
                    <ChoiceCard key={c.id} active={concern === c.id} onClick={() => setConcern(c.id)} testId={`express-concern-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{c.emoji}</span>
                        <span className="font-medium">{c.label}</span>
                      </div>
                    </ChoiceCard>
                  ))}
                </div>
              </>
            )}

            {/* === Étape 4 : Type de peau === */}
            {step === 4 && (
              <>
                <StepHeader step={4} total={TOTAL_STEPS} title="Type de peau ?" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="express-skin-type-grid">
                  {SKIN_TYPES.map((s) => (
                    <ChoiceCard key={s.id} active={skinType === s.id} onClick={() => setSkinType(s.id)} testId={`express-skin-${s.id}`}>
                      <div className="font-medium">{s.label}</div>
                    </ChoiceCard>
                  ))}
                </div>
              </>
            )}

            {/* === Étape 5 : Allergie === */}
            {step === 5 && (
              <>
                <StepHeader step={5} total={TOTAL_STEPS} title="Allergie connue ?" subtitle="À un produit ou ingrédient cosmétique." />
                <div className="grid grid-cols-3 gap-2 mb-3" data-testid="express-allergy-grid">
                  {ALLERGY_OPTIONS.map((a) => (
                    <ChoiceCard key={a.id} active={allergy === a.id} onClick={() => setAllergy(a.id)} testId={`express-allergy-${a.id}`}>
                      <div className="text-center font-medium">{a.label}</div>
                    </ChoiceCard>
                  ))}
                </div>
                {allergy === 'oui' && (
                  <div>
                    <Label className="text-xs font-medium">Quel produit / ingrédient ?</Label>
                    <Input value={allergyDetail} onChange={(e) => setAllergyDetail(e.target.value)} placeholder="Ex : parfums, lanoline, conservateurs..." data-testid="express-allergy-detail" />
                  </div>
                )}
              </>
            )}

            {/* === Navigation === */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-100">
              <Button variant="ghost" onClick={prev} disabled={step <= (embedded ? 2 : 1) || submitting} data-testid="express-prev">
                <ArrowLeft className="w-4 h-4 mr-1" /> Précédent
              </Button>
              {step < TOTAL_STEPS ? (
                <Button
                  onClick={next}
                  disabled={
                    (step === 1 && !canNextStep1()) ||
                    (step === 2 && !zone) ||
                    (step === 3 && !concern) ||
                    (step === 4 && !skinType)
                  }
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="express-next"
                >
                  Suivant <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!allergy || submitting}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  data-testid="express-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Obtenir la recommandation
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
    </>
  );

  if (embedded) {
    return <div data-testid="express-diagnosis-embedded">{stepsContent}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 py-6 px-4" data-testid="express-diagnosis-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="express-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Quitter
          </Button>
          <div className="flex items-center gap-1 text-amber-700">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-bold tracking-wide uppercase">Mode Express</span>
          </div>
        </div>
        {stepsContent}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
// RÉSULTAT FINAL — Carte client photographiable
// ════════════════════════════════════════════════════════════════════════
const ExpressResultCard = ({ result, onBack, onRestart }) => {
  const { client, generated_client_code, diagnosis, pharmacy } = result;
  const code = generated_client_code || client.client_code;

  const copyCode = () => {
    navigator.clipboard?.writeText(code);
    toast.success('Code copié');
  };

  const printCard = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 py-6 px-4" data-testid="express-result-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <Button variant="outline" size="sm" onClick={onRestart} data-testid="express-restart">
            <Sparkles className="w-4 h-4 mr-1" /> Nouveau diagnostic
          </Button>
        </div>

        {/* === CARTE CLIENT (photographiable) === */}
        <Card
          className="shadow-xl border-2 border-amber-300 overflow-hidden mb-4"
          data-testid="express-client-card"
          id="express-printable-card"
        >
          {/* Header pharmacie */}
          <div className="bg-gradient-to-r from-amber-600 to-orange-700 text-white px-6 py-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  <h2 className="text-lg font-bold">{pharmacy?.name || 'Pharmacie Saint Ange Des Amis'}</h2>
                </div>
                <p className="text-xs text-amber-100 mt-0.5">{pharmacy?.tagline || 'Votre santé, notre engagement'}</p>
              </div>
              <Sparkles className="w-6 h-6 text-amber-200" />
            </div>
          </div>

          <CardContent className="p-6 space-y-4 bg-white">
            {/* Code client en grand (si généré ou existant) */}
            {code && (
              <div className="bg-amber-50 border-2 border-dashed border-amber-400 rounded-xl p-5 text-center" data-testid="express-result-code-block">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-2">Votre code client</p>
                <p className="text-4xl sm:text-5xl font-bold text-amber-900 font-mono tracking-wider mb-3" data-testid="express-result-code">{code}</p>
                <div className="flex items-center justify-center gap-2 print:hidden">
                  <Button size="sm" variant="outline" onClick={copyCode} data-testid="express-copy-code">
                    <Copy className="w-3 h-3 mr-1" /> Copier
                  </Button>
                  <Button size="sm" variant="outline" onClick={printCard}>
                    <Printer className="w-3 h-3 mr-1" /> Imprimer
                  </Button>
                </div>
                <div className="flex items-center justify-center gap-1 mt-3 text-amber-700 text-xs print:hidden">
                  <Camera className="w-3.5 h-3.5" />
                  <span>📸 Conservez ce code, prenez-le en photo ou notez-le.</span>
                </div>
                {generated_client_code && (
                  <p className="text-[10px] text-zinc-500 mt-2 italic">Ce code permet de retrouver votre fiche à votre prochain passage.</p>
                )}
              </div>
            )}

            {/* Recap client */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[10px] text-zinc-500 uppercase">Client</p>
                <p className="font-semibold">{client.first_name}</p>
              </div>
              {client.phone && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Téléphone</p>
                  <p className="font-medium">{client.phone}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] text-zinc-500 uppercase">Préoccupation</p>
                <p className="font-medium capitalize">{diagnosis.concern.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase">Zone · Type</p>
                <p className="font-medium capitalize">{diagnosis.zone} · {diagnosis.skin_type}</p>
              </div>
            </div>

            {/* Conseils */}
            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-4" data-testid="express-advice">
              <p className="text-xs font-bold text-emerald-700 uppercase mb-1">Conseil personnalisé</p>
              <p className="text-sm text-emerald-900">{diagnosis.advice}</p>
            </div>

            {/* Allergie alerte */}
            {diagnosis.allergy === 'oui' && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                <p className="text-xs text-red-900">
                  Allergie déclarée — toujours vérifier la composition AVANT achat. Demander conseil au pharmacien si doute.
                </p>
              </div>
            )}

            {/* Produits suggérés */}
            {diagnosis.products?.length > 0 && (
              <div>
                <p className="text-xs font-bold text-zinc-700 uppercase mb-2">Produits suggérés ({diagnosis.products.length})</p>
                <div className="space-y-1.5" data-testid="express-products">
                  {diagnosis.products.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg" data-testid={`express-product-${p.id}`}>
                      {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        {p.brand && <p className="text-[10px] text-zinc-500">{p.brand}</p>}
                      </div>
                      {p.price != null && <p className="text-sm font-bold text-amber-700">{Number(p.price).toLocaleString('fr-FR')} F</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ingrédients à éviter */}
            {diagnosis.ingredients_to_avoid?.length > 0 && (
              <div className="text-xs text-zinc-500 pt-3 border-t border-zinc-100">
                <strong>À éviter :</strong> {diagnosis.ingredients_to_avoid.join(' · ')}
              </div>
            )}

            {/* Footer pharmacie */}
            <div className="text-center pt-3 border-t border-amber-200">
              <p className="text-[10px] text-zinc-500">
                Pour toute question, contactez notre équipe en pharmacie.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-zinc-500 print:hidden">
          Diagnostic express · La photo et le diagnostic complet restent disponibles à tout moment.
        </p>
      </div>
    </div>
  );
};

export default ExpressDiagnosisPage;
